import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { forkJoin } from 'rxjs';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';


@Component({
  selector: 'projects-checklist',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent
  ],
  templateUrl: './projects-checklist.component.html',
  styleUrl: './projects-checklist.component.scss'
})
export class ProjectsChecklistComponent implements OnInit, OnChanges {
  @Input() projectId?: any;
  @Input() jurisdictionId?: string | number | null;
  checklist: any[] = [];
  checklistForms: { [key: number]: FormGroup } = {};

  private pendingRestoreAssignTo: Map<number, number | null> = new Map();

  employeeOptions: { key: string, value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService
  ) { }

  submitForm(item?: any, index?: number, onSuccess?: () => void): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is required');
      return;
    }

    const updateRequest = item !== undefined
      ? this.getUpdateRequestForItem(item, index ?? this.getItemIndex(item))
      : this.getUpdateRequestsForAllItems();

    if (!updateRequest) {
      this.alertNotificationService.warning(item ? 'Please select a valid assignee before continuing' : 'No checklists to update');
      return;
    }

    updateRequest.subscribe({
      next: () => {
        this.alertNotificationService.success(item ? 'Checklist updated successfully' : 'All checklists updated successfully');
        onSuccess?.();
      },
      error: (error) => {
        console.error('Error updating checklists:', error);
        this.alertNotificationService.error('Error updating checklists: ' + (error.message || 'Unknown error'));
      }
    });
  }

  ngOnInit() {
    if (this.projectId) {
      this.projectsService.listProjectTeams(Number(this.projectId)).subscribe({
        next: (response) => {
          if (response?.data?.projectTeams) {
            this.employeeOptions = response.data.projectTeams.map((emp: any) => ({
              key: emp.projectTeamId,
              value: emp.employee.firstName + ' ' + emp.employee.lastName
            }));
          }
        },
        error: (error) => {
          console.error('Error loading employees:', error);
        }
      });

      this.loadChecklists(this.jurisdictionId);
    }
  }

  reloadWithJurisdiction(jurisdictionId?: string | number | null): void {
    this.loadChecklists(jurisdictionId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jurisdictionId'] && !changes['jurisdictionId'].firstChange && this.projectId) {
      this.loadChecklists(this.jurisdictionId);
    }
  }

  loadChecklists(jurisdictionId?: string | number | null): void {
    if (!this.projectId) {
      return;
    }

    let params = new HttpParams();
    if (jurisdictionId !== null && jurisdictionId !== undefined && jurisdictionId !== '') {
      params = params.set('jurisdictionId', `${jurisdictionId}`);
    }

    this.projectsService.listProjectChecklists(this.projectId, { params }).subscribe({
      next: (response) => {
        this.checklist = response?.data?.projectChecklists ?? [];
        this.checklistForms = {};
        this.checklist.forEach((item, index) => {
          const key = item?.projectChecklistId ?? index;
          this.checklistForms[key] = this.createFormGroup(item);
          const savedAssignTo = this.pendingRestoreAssignTo.get(key);
          if (savedAssignTo !== undefined) {
            this.checklistForms[key].get('assignTo')?.setValue(savedAssignTo);
          } else {
            this.checklistForms[key].get('assignTo')?.setValue(this.normalizeAssignToValue(item?.projectTeam?.projectTeamId));
          }
        });
        this.pendingRestoreAssignTo.clear();
      },
      error: (error) => {
        console.error('Error loading checklists:', error);
      }
    });
  }

  createFormGroup(item: any): FormGroup {
    return this.fb.group({
      checklist: [{ value: item?.projectWorkinglist?.checklist?.name ?? '', disabled: true }],
      workinglistName: [{ value: item?.projectWorkinglist?.name ?? '', disabled: true }],
      platform: [{ value: item?.projectWorkinglist?.platform?.name ?? 'N/A', disabled: true }],
      assignTo: [item?.projectTeamId ?? null, Validators.required],
      statusTest: [item?.status ?? null]
    });
  }

  getFormControl(formGroup: FormGroup, controlName: string): FormControl {
    return formGroup.get(controlName) as FormControl;
  }

  onAssignToChange(item: any, newValue: string) {
    /*this.projectsService.updateChecklist(item.checklist_id, { assign_to: newValue }).subscribe({
      next: (response) => {
        console.log('Checklist updated successfully');
      },
      error: (error) => {
        console.error('Error updating checklist:', error);
      }
    });*/
  }

  isCreateTestlistDisabled(item: any, index: number): boolean {
    return item?.isCreateTestlistEnabled === false;
  }

  getStatusClass(status: 'FAILED' | 'PENDING' | 'PASSED' | 'TO_ASSIGN' | null): string {
    switch (status) {
      case 'FAILED':
        return 'status-failed';
      case 'PENDING':
        return 'status-pending';
      case 'PASSED':
        return 'status-passed';
      case 'TO_ASSIGN':
        return '';
      default:
        return '';
    }
  }

  exportToExcel() {
    console.log('Export to Excel clicked');
  }

  createTestlist(item: any) {
    if (!this.projectId || !item?.projectChecklistId) {
      this.alertNotificationService.error('Project ID or checklist ID is missing');
      return;
    }

    const itemIndex = this.getItemIndex(item);
    this.submitForm(item, itemIndex, () => {
      if (!this.projectId || !item?.projectChecklistId) {
        return;
      }

      const key = item?.projectChecklistId ?? itemIndex;
      const form = this.checklistForms[key];
      const testerProjectTeamId = this.normalizeAssignToValue(form?.get('assignTo')?.value);

        this.projectsService
          .createProjectTestlistFromCheckList(Number(this.projectId), Number(item.projectChecklistId), { testerProjectTeamId })
          .subscribe({
            next: () => {
              this.alertNotificationService.success('Testlist created successfully');
              this.checklist.forEach(cl => {
                if (cl.projectChecklistId === item.projectChecklistId) return;
                const key = cl.projectChecklistId;
                const form = this.checklistForms[key];
                if (form) {
                  this.pendingRestoreAssignTo.set(key, this.normalizeAssignToValue(form.get('assignTo')?.value));
                }
              });
              this.loadChecklists(this.jurisdictionId);
            },
            error: (error) => {
              console.error('Error creating testlist:', error);
              this.alertNotificationService.error('Error creating testlist: ' + (error.message || 'Unknown error'));
            }
          });
    });
  }

  requestDeleteItem(item: any, index: number): void {
    const checklistName = item.projectWorkinglist?.checklist?.name || 'this item';
    const workinglistName = item.projectWorkinglist?.name || '';
    this.confirmationDialogService.open(
      `Are you sure you want to delete "<strong>${checklistName}</strong>"${workinglistName ? ` - ${workinglistName}` : ''}?`,
      () => this.deleteItem(item, index)
    );
  }

  private deleteItem(item: any, index: number): void {
    if (!this.projectId || !item.projectChecklistId) {
      this.alertNotificationService.error('Unable to delete: missing project or checklist ID');
      return;
    }

    this.projectsService.deleteProjectChecklist(Number(this.projectId), item.projectChecklistId).subscribe({
      next: (response) => {
        const key = item?.projectChecklistId ?? index;
        this.checklist.splice(index, 1);
        delete this.checklistForms[key];
        
        this.alertNotificationService.success('Checklist item deleted successfully');
      },
      error: (error) => {
        console.error('Error deleting checklist item:', error);
        this.alertNotificationService.error('Error deleting checklist item: ' + (error.message || 'Unknown error'));
      }
    });
  }

  private getItemIndex(item: any): number {
    return this.checklist.findIndex((checklistItem) => checklistItem?.projectChecklistId === item?.projectChecklistId);
  }

  private getUpdateRequestsForAllItems(): Observable<any> | null {
    const updateRequests: Observable<any>[] = [];

    this.checklist.forEach((item, index) => {
      const request = this.getUpdateRequestForItem(item, index);
      if (request) {
        updateRequests.push(request);
      }
    });

    if (updateRequests.length === 0) {
      return null;
    }

    return forkJoin(updateRequests);
  }

  private getUpdateRequestForItem(item: any, index: number): Observable<any> | null {
    if (!item?.projectChecklistId) {
      return null;
    }

    const key = item?.projectChecklistId ?? index;
    const form = this.checklistForms[key];
    if (!form) {
      return null;
    }

    const projectTeamId = this.normalizeAssignToValue(form.get('assignTo')?.value);
    if (projectTeamId === null) {
      return null;
    }

    return this.projectsService.updateProjectChecklist(
      Number(this.projectId),
      Number(item.projectChecklistId),
      { projectTeamId }
    );
  }

  private normalizeAssignToValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }
}
